import { MedplumClient } from "@medplum/core";
import type { ThrottleStrategy } from "@/lib/medplum/retry";
import { createRetryFetch } from "@/lib/medplum/retry";
import { ServerSideStorage } from "@/lib/medplum/storage";

//-----------------------------------------------------------------------------------------------
//                                     TYPES
//-----------------------------------------------------------------------------------------------

type Audience = "user" | "background";

/**
 * Where every Medplum client points when `MEDPLUM_BASE_URL` is unset: hosted Medplum. Exported
 * because a safety guard deciding whether a write is local has to resolve the SAME url the client
 * will actually use - a guard holding its own copy silently disagrees the moment one of the two is
 * edited, and that disagreement fails open. See `scripts/local/seed-safety.ts`.
 */
export const DEFAULT_MEDPLUM_BASE_URL = "https://api.medplum.com/";

export interface ClientOptions {
  audience: Audience;
  /**
   * Where rate-limit waits should happen. When `"durable"`, long throttle
   * delays throw Inngest `RetryAfterError` instead of sleeping in-process.
   * Only meaningful inside Inngest background job handlers.
   */
  throttling?: ThrottleStrategy;
  /**
   * Milliseconds a GET result stays in the client's cache. Default 0: every read goes to Medplum.
   * Set it only on a client scoped to one job run that re-reads the same resources (for example
   * the same Practitioner across many threads). A patch still reads fresh: `patchResource`
   * invalidates the URL before its read.
   */
  cacheTime?: number;
}

interface ClientProfile {
  clientIdEnv: string;
  clientSecretEnv: string;
  /** Fallback env var names when the primary ones are not set. */
  fallbackClientIdEnv?: string;
  fallbackClientSecretEnv?: string;
  /** Human-readable context used in error messages. */
  context: string;
}

//-----------------------------------------------------------------------------------------------
//                                     CLIENT PROFILES
//-----------------------------------------------------------------------------------------------

/**
 * Maps audience to environment variable names for credentials.
 *
 * - **user**: The existing backend client credentials, used for user-facing server
 *   actions, API routes, and any request where a user is waiting on the response.
 * - **background**: A separate set of credentials for background jobs, crons,
 *   syncs, and other async work that doesn't block a user.
 */
const CLIENT_PROFILES: Record<Audience, ClientProfile> = {
  user: {
    clientIdEnv: "MEDPLUM_BACKEND_CLIENT_ID",
    clientSecretEnv: "MEDPLUM_BACKEND_CLIENT_SECRET",
    context: "user-facing Medplum client",
  },
  background: {
    clientIdEnv: "MEDPLUM_BACKGROUND_CLIENT_ID",
    clientSecretEnv: "MEDPLUM_BACKGROUND_CLIENT_SECRET",
    fallbackClientIdEnv: "MEDPLUM_ASYNC_CLIENT_ID",
    fallbackClientSecretEnv: "MEDPLUM_ASYNC_CLIENT_SECRET",
    context: "background Medplum client",
  },
};

//-----------------------------------------------------------------------------------------------
//                                     PUBLIC API
//-----------------------------------------------------------------------------------------------

/**
 * Creates a Medplum client for a specific audience.
 *
 * Each audience maps to a dedicated set of client credentials so that
 * Medplum quota can be managed independently per workload type.
 *
 * @example
 * ```typescript
 * // User-facing server actions and API routes
 * const medplum = createMedplumClient({ audience: "user" });
 *
 * // Background jobs, crons, syncs, migrations
 * const medplum = createMedplumClient({ audience: "background" });
 * ```
 */
export function createMedplumClient(options: ClientOptions): MedplumClient {
  const profile = CLIENT_PROFILES[options.audience];
  if (!profile) {
    throw new Error(`Unknown Medplum client audience: ${options.audience}`);
  }

  return createMedplumClientFromProfile(profile, options);
}

/** Alias for the ordinary user-facing backend client used by narrow server-side reads/writes. */
export function createMedplumBackendClient(): MedplumClient {
  return createMedplumClient({ audience: "user" });
}

/**
 * Alias for the background/async client used by migrations, crons, and bulk work.
 * Equivalent to `createMedplumClient({ audience: "background" })`.
 */
export function createMedplumAsyncClient(options?: {
  throttling?: ThrottleStrategy;
}): MedplumClient {
  return createMedplumClient({ audience: "background", ...options });
}

/**
 * Resolves the client credentials for a given audience using the same fallback
 * chain as `createMedplumClient`. Use this when you need the raw `clientId` /
 * `clientSecret` pair (e.g. to call `startClientLogin`) without duplicating the
 * resolution logic.
 */
export function resolveClientCredentials(options: ClientOptions): {
  clientId: string;
  clientSecret: string;
} {
  const profile = CLIENT_PROFILES[options.audience];
  if (!profile) {
    throw new Error(`Unknown Medplum client audience: ${options.audience}`);
  }

  return resolveProfileCredentials(profile);
}

//-----------------------------------------------------------------------------------------------
//                                     INTERNALS
//-----------------------------------------------------------------------------------------------

/**
 * Resolves client credentials from a profile, trying primary env vars first then fallbacks.
 * Both ID and secret must come from the same source to avoid mixing credentials from different
 * OAuth clients.
 */
function resolveProfileCredentials(profile: ClientProfile): {
  clientId: string;
  clientSecret: string;
} {
  const primaryId = process.env[profile.clientIdEnv];
  const primarySecret = process.env[profile.clientSecretEnv];
  const hasPrimary = primaryId && primarySecret;

  const fallbackId = profile.fallbackClientIdEnv
    ? process.env[profile.fallbackClientIdEnv]
    : undefined;
  const fallbackSecret = profile.fallbackClientSecretEnv
    ? process.env[profile.fallbackClientSecretEnv]
    : undefined;
  const hasFallback = fallbackId && fallbackSecret;

  let clientId: string | undefined;
  let clientSecret: string | undefined;
  if (hasPrimary) {
    clientId = primaryId;
    clientSecret = primarySecret;
  } else if (hasFallback) {
    clientId = fallbackId;
    clientSecret = fallbackSecret;
  }

  if (!clientId || !clientSecret) {
    const envNames = [profile.clientIdEnv, profile.clientSecretEnv];
    if (profile.fallbackClientIdEnv) {
      envNames.push(profile.fallbackClientIdEnv, profile.fallbackClientSecretEnv!);
    }
    throw new Error(
      `Missing credentials for ${profile.context}. Set ${envNames.join(" / ")} environment variables.`,
    );
  }

  return { clientId, clientSecret };
}

/** Builds a MedplumClient with basic auth from a resolved profile. */
function createMedplumClientFromProfile(
  profile: ClientProfile,
  { audience, throttling, cacheTime = 0 }: ClientOptions,
): MedplumClient {
  const { clientId, clientSecret } = resolveProfileCredentials(profile);

  const client = new MedplumClient({
    baseUrl: process.env.MEDPLUM_BASE_URL || DEFAULT_MEDPLUM_BASE_URL,
    fetch: createRetryFetch({
      label: audience,
      ...(throttling === "durable" && { throttling: "durable" }),
    }),
    cacheTime,
    storage: new ServerSideStorage(),
  });

  client.setBasicAuth(clientId, clientSecret);
  return client;
}
